import { FlameDetectorConfiguration } from "@domain/project/configurations/flame-detector-configuration";
import { Floorplan } from "@domain/project/floorplan/floorplan";
import { FloorplanEventType } from "@domain/project/floorplan/floorplan-event";
import { FloorplanItem } from "@domain/project/floorplan/floorplan-item";
import { FloorplanProductItem } from "@domain/project/floorplan/floorplan-product-item";
import { DEFAULT_FLAME_DETECTOR_CONE_ROTATION } from "@lib/constants/flame_detector_defaults";
import { Exclude, Expose, Transform, Type } from "class-transformer";
import { IsNumber, IsOptional, IsString, ValidateNested } from "class-validator";
import { Subject, Subscription } from "rxjs";
import { v4 as uuidv4 } from "uuid";

export class FloorplanFlameDetector extends FloorplanProductItem<FlameDetectorConfiguration> {
  @Exclude()
  DEFAULT_COVERAGE_AREA_LENGTH: number = 1;

  @Type(() => FlameDetectorConfiguration)
  @ValidateNested()
  @Exclude()
  private _flameDetectorConfiguration: FlameDetectorConfiguration;

  @IsString()
  @Expose({ name: "flameDetectorId" })
  private readonly _flameDetectorId: string;

  @IsNumber()
  @Expose({ name: "coverageAreaLength" })
  @Transform((params) => params.value || 375, { toClassOnly: true })
  private _coverageAreaLength: number = 375;

  @IsString()
  @IsOptional()
  @Expose({ name: "sensitivity" })
  private _sensitivity?: string;

  @IsString()
  @IsOptional()
  @Expose({ name: "mountingHeight" })
  private _mountingHeight?: string;

  @IsNumber()
  @Expose({ name: "coneRotation" })
  @Transform((params) => params.value ?? DEFAULT_FLAME_DETECTOR_CONE_ROTATION, { toClassOnly: true })
  private _coneRotation: number = DEFAULT_FLAME_DETECTOR_CONE_ROTATION;

  @Exclude()
  private referenceMeasureSub?: Subscription;

  @IsNumber()
  private _referenceCoverageAreaLength: number = this.DEFAULT_COVERAGE_AREA_LENGTH;

  @IsNumber()
  @IsOptional()
  @Expose({ name: "referenceCoverageAreaLengthOverride" })
  private _referenceCoverageAreaLengthOverride?: number;

  @Exclude()
  public coverageAreaLengthChanged: Subject<{ length: number }>;

  @Exclude()
  referenceCoverageAreaLengthChanged: Subject<{ referenceLength: number }>;

  constructor(
    floorplan: Floorplan,
    flameDetector: FlameDetectorConfiguration,
    id: string,
    x: number,
    y: number,
    size: number,
    coverageAreaLength: number = 375,
    sensitivity?: string,
    mountingHeight?: string,
    notes?: string,
  ) {
    super(floorplan, flameDetector, id, x, y, size, notes);
    this._flameDetectorId = flameDetector?.id;
    this._flameDetectorConfiguration = flameDetector;
    this._coverageAreaLength = coverageAreaLength;
    this._referenceCoverageAreaLength = flameDetector?.defaultCoverageAreaLength ?? this.DEFAULT_COVERAGE_AREA_LENGTH;
    this._sensitivity = sensitivity;
    this._mountingHeight = mountingHeight;
    this._coneRotation = DEFAULT_FLAME_DETECTOR_CONE_ROTATION;
    this.coverageAreaLengthChanged = new Subject();
    this.referenceCoverageAreaLengthChanged = new Subject();
    this.updateCoverageAreaLengthByReferenceMeasure();
  }

  setItemTextVisibility() {
    this.itemTextVisible = this._floorplan.floorplanState.flameDetectorsItemTextVisible;
  }

  override init(floorplan: Floorplan) {
    super.init(floorplan);
    if (!this.config) {
      return;
    }
    this.applyInitialDetectionRange();
  }

  protected override getConfigFromProject(floorplan: Floorplan): FlameDetectorConfiguration | undefined {
    return floorplan.getFlameDetectorConfig(this._flameDetectorId);
  }

  protected override createRawDuplicate(): ((x: number, y: number) => FloorplanItem) | undefined {
    const config = this.config;
    const sensitivity = this.sensitivity;
    const mountingHeight = this.mountingHeight;
    const coverageAreaLength = this.coverageAreaLength;
    const size = this.size;

    return (x: number, y: number) =>
      FloorplanFlameDetector.create(this._floorplan, config, x, y, size, coverageAreaLength, sensitivity, mountingHeight);
  }

  protected override afterDuplicateAdded(): ((duplicate: FloorplanItem) => void) | undefined {
    const afterDuplicateAdded = super.afterDuplicateAdded();
    const referenceCoverageAreaLength = this.isCoverageAreaOverridden ? this.referenceCoverageAreaLength : undefined;

    return (duplicate: FloorplanItem) => {
      const duplicateFlameDetector = duplicate as FloorplanFlameDetector;
      afterDuplicateAdded?.(duplicateFlameDetector);

      if (referenceCoverageAreaLength !== undefined) {
        duplicateFlameDetector.updateCoverageAreaLength(referenceCoverageAreaLength);
      }
    };
  }

  override getDuplicateTargetCollection() {
    return this._floorplan.flameDetectors;
  }

  protected override registerDuplicate() {
    return (duplicate: FloorplanItem, afterDuplicateAdded?: (duplicate: FloorplanItem) => void) =>
      this.registerDuplicateInFloorplan(
        duplicate as FloorplanFlameDetector,
        this._floorplan.flameDetectors,
        FloorplanEventType.FLAME_DETECTOR_ADDED,
        () => this._floorplan.project.setPositionNumbers(),
        afterDuplicateAdded as ((duplicate: FloorplanFlameDetector) => void) | undefined,
      );
  }

  override delete() {
    this._floorplan.deleteFlameDetector(this);
  }

  get coverageAreaLength(): number {
    return this._coverageAreaLength;
  }

  set coverageAreaLength(value: number) {
    if (this._floorplan.referenceMeasure) {
      value = this.calculateCoverageArea();
    }
    this._coverageAreaLength = value;
    this._floorplan.publishUpdate(FloorplanEventType.ITEM_UPDATED, this);
  }

  get referenceCoverageAreaLength() {
    return this._referenceCoverageAreaLengthOverride ?? this._referenceCoverageAreaLength;
  }

  get isDetectionLengthOverridden(): boolean {
    return this._referenceCoverageAreaLengthOverride !== undefined;
  }

  get isCoverageAreaOverridden(): boolean {
    return this._referenceCoverageAreaLengthOverride !== undefined;
  }

  get sensitivity(): string | undefined {
    return this._sensitivity;
  }

  set sensitivity(value: string) {
    this._sensitivity = value;
  }

  get mountingHeight(): string | undefined {
    return this._mountingHeight;
  }

  set mountingHeight(value: string) {
    this._mountingHeight = value;
  }

  get coneRotation(): number {
    return this._coneRotation;
  }

  set coneRotation(value: number) {
    this._coneRotation = value;
    this._floorplan.publishUpdate(FloorplanEventType.ITEM_UPDATED, this);
  }

  public isMonitoringAreaScaleAble(): boolean {
    return !this._floorplan.referenceMeasure;
  }

  isReferenceMeasureSet() {
    if (this._floorplan) return !!this._floorplan.referenceMeasure;
    return false;
  }

  updateCoverageAreaLength(radius: number) {
    this._referenceCoverageAreaLengthOverride = radius;
    this._referenceCoverageAreaLength = radius;
    if (this.isReferenceMeasureSet()) {
      this._coverageAreaLength = radius * this._floorplan.getReferenceScale();
    }
    this._floorplan.publishUpdate(FloorplanEventType.COVERAGE_AREA_LENGTH_UPDATED, this);
    this.coverageAreaLengthChanged.next({ length: this.referenceCoverageAreaLength });
  }

  resetDetectionLengthToDefault() {
    this._referenceCoverageAreaLengthOverride = undefined;
    this._referenceCoverageAreaLength = this.config.defaultCoverageAreaLength;
    this.updateCoverageAreaLengthByReferenceMeasure();
  }

  setDetectionLengthOverrideFromCurrent() {
    this.updateCoverageAreaLength(this.referenceCoverageAreaLength);
  }

  applyDefaultDetectionLength() {
    if (this._referenceCoverageAreaLengthOverride !== undefined) {
      return;
    }
    this._referenceCoverageAreaLength = this.config.defaultCoverageAreaLength;
    this.updateCoverageAreaLengthByReferenceMeasure();
  }

  updateCoverageAreaLengthByReferenceMeasure(publishUpdate = true): void {
    if (this.isReferenceMeasureSet()) {
      this._coverageAreaLength = this.calculateCoverageAreaLengthByReference();
      if (publishUpdate) {
        this._floorplan.publishUpdate(FloorplanEventType.COVERAGE_AREA_LENGTH_UPDATED, this);
      }
      this.coverageAreaLengthChanged.next({ length: this.referenceCoverageAreaLength });
    }
  }

  static create(
    floorplan: Floorplan,
    flameDetector: FlameDetectorConfiguration,
    x: number,
    y: number,
    size: number,
    coverageAreaLength: number,
    sensitivity?: string,
    mountingHeight?: string,
  ): FloorplanFlameDetector {
    return new FloorplanFlameDetector(
      floorplan,
      flameDetector,
      uuidv4(),
      x,
      y,
      size,
      coverageAreaLength,
      sensitivity,
      mountingHeight,
    );
  }

  protected override refreshFloorplanLockState() {
    this._floorplan.floorplanState.refreshFlameDetectorsLock();
  }

  private calculateCoverageAreaLengthByReference(): number {
    return Math.round(this.referenceCoverageAreaLength * this._floorplan.getReferenceScale() * 100) / 100;
  }

  private calculateCoverageArea(): number {
    return Math.round((this.coverageAreaLength / this._floorplan.getReferenceScale()) * 100) / 100;
  }

  private applyInitialDetectionRange() {
    const defaultLength = this.config.defaultCoverageAreaLength;

    if (this._referenceCoverageAreaLengthOverride !== undefined) {
      this.updateCoverageAreaLengthByReferenceMeasure(false);
      return;
    }

    if (!this._floorplan.referenceMeasure) {
      this._referenceCoverageAreaLength = defaultLength;
      return;
    }

    const inferredReferenceRadius = this.inferReferenceLengthFromMonitoringArea();

    if (inferredReferenceRadius !== undefined && this.shouldOverrideReferenceLength(inferredReferenceRadius, defaultLength)) {
      this._referenceCoverageAreaLengthOverride = inferredReferenceRadius;
      this._referenceCoverageAreaLength = inferredReferenceRadius;
    } else {
      this._referenceCoverageAreaLength = defaultLength;
    }

    this.updateCoverageAreaLengthByReferenceMeasure(false);
  }

  private inferReferenceLengthFromMonitoringArea(): number | undefined {
    const referenceScale = this._floorplan.getReferenceScale();
    return referenceScale ? this._coverageAreaLength / referenceScale : undefined;
  }

  private shouldOverrideReferenceLength(inferredReferenceLength: number | undefined, defaultLength: number): boolean {
    if (inferredReferenceLength === undefined) {
      return false;
    }
    const difference = Math.abs(inferredReferenceLength - defaultLength);
    return difference > 0.001;
  }
}
